// src/pages/Admin/Step1BusinessEntity.js

import React from "react";
import { Form, Formik, FieldArray } from "formik";
import * as Yup from "yup";
import isEqual from 'lodash/isEqual';
import {
    Box, Grid, TextField, Typography, Autocomplete, Button,
    InputAdornment, // for phone input
} from "@mui/material";
import Select from "react-select";
import { Log, getCountryList, getEntityFilterConditions, sendDataWithPost, checkUENExists } from "../../services/services";
import CustomDatePicker from "../../components/form/CustomDatePicker";
import moment from "moment";
import { urlRegex, INTERNATIONAL_COUNTRY_LIST, MAPPED_REGISTERED_COUNTRY, RISK_LEVEL } from "../../utils/constants";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import metadata from 'libphonenumber-js/metadata.full.json'
import '../../pages/Admin/PhoneInput.css';
import { styles } from "../../utils/styles";

const DRAFT_KEY = 'onboarding_draft';

const loadStepData = (stepIndex) => {
    try {
        const raw = window.localStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        return data.allFormData ? data.allFormData[stepIndex] : null;
    } catch (e) {
        Log.error('Failed to load step data from storage:', e);
        return null;
    }
};

// use API to convert code ISO -> country name
const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
const PRIORITY_CODES = ["SG", "MY", "TH"];
const allCountries = getCountries(metadata)
    .map((code) => ({
        code,
        label: regionNames.of(code),
        dialCode: "+" + getCountryCallingCode(code, metadata),
    }))
    // exclude +247
    .filter((country) => country.dialCode !== "+247");

// Split priority 
const priorityCountries = PRIORITY_CODES.map((pCode) =>
    allCountries.find((c) => c.code === pCode)
).filter(Boolean);
const otherCountries = allCountries.filter(
    (c) => !PRIORITY_CODES.includes(c.code)
);

// Join priority countries and others
export const COUNTRIES = [...priorityCountries, ...otherCountries];

// Validation Schema for Merchant (moved from original file)
const MerchantSchema = Yup.object().shape({
    name: Yup.string().required("Merchant name is required.").trim(),
    number_of_terminals: Yup.number()
        .required("The number of terminals is required.")
        .min(1, "There must be at least 1 terminal.")
        .typeError("Must be a number."),
    address_same_as_business_entity: Yup.boolean(),
    address_line1: Yup.string().when("address_same_as_business_entity", {
        is: false,
        then: (schema) => schema.required("Line 1 address is required if not copied."),
    }).trim(),
    // NOTE: Keep merchants array validation here as it's part of the Step 1 form data/initialValues
});


// Validation Schema for Step 1
const Step1ValidationSchema = Yup.object().shape({
    business_name: Yup.string().required("Required").trim(),
    uen: Yup.string()
        .required("Required")
        .matches(/^[a-zA-Z0-9]{1,13}$/, 'Only Alphabets and numbers are allowed')
        .max(13, "Length of Registered ID should be 13 alphanumeric characters")
        .min(3, "Length of Registered ID should be at least 3 alphanumeric characters")
        //TODO: only check uen when submit compliance
        // .test('unique-uen', 'This Registered ID already exists', async function (value) {
        //     if (!value) return true;
        //     const exists = await checkUENExists(value); // Assuming checkUENExists returns a boolean
        //     return !exists;
        // })
        ,
    registered_country: Yup.string().required("Required"), // Changed to object due to Autocomplete
    address_1: Yup.string().required("Required").trim(),
    address_2: Yup.string().required("Required").trim(),
    contact_name: Yup.string().required("Required").trim(),
    contact_email: Yup.string().email().required("Required").trim(),
    gdrive_link: Yup.string().nullable().matches(urlRegex, "Please enter a valid link"),
    number_of_merchants: Yup.number().required("Required").min(1, "Must be greater than or equal to 1"),
    application_type: Yup.string().nullable().required("Application Type is required"),
    // merchants: Yup.array().of(MerchantSchema),
    // merchant_payout: Yup.array().min(1, "Merchant Payout is required"),
    // bank_accounts: Yup.array().min(1, "Bank Account is required"),
    // last_kyc_review: Yup.string().nullable().when("next_kyc_review", {
    //     is: (val) => !!val,
    //     then: (schema) => schema.test("check", "Last KYC cannot be after Next KYC", function (val) {
    //         const { last_kyc_review, next_kyc_review } = this.parent;
    //         if (!last_kyc_review || !next_kyc_review) return true;
    //         return !(moment(last_kyc_review).isAfter(moment(next_kyc_review), 'day'))
    //     })
    // }),
});

const Step1BusinessEntity = ({ onNext, onSkip, formData, isSubmitting, countryList, entityFilterConditions, bankAccounts, COUNTRIES, APPLICATION_TYPES, readOnly = false }) => {

    // Initial values based on the original form structure
    // NOTE: Need to map string values back to object structure for Autocomplete/Select
    const initialValues = loadStepData(0) || {
        onboarding_application_id: "",
        business_entity_id: "",
        number_of_merchants: 1,
        application_type: "ONLINE",
        business_name: "",
        short_name: "",
        uen: "",
        address_1: "",
        address_2: "",
        address_3: "",
        contact_name: "",
        contact_number: "+65",
        contact_email: "",
        registered_country: "",
        postal_code: "",
        address_country: "",
        gdrive_link: "",
        merchant_payout: [],
        bank_accounts: [],
        date_of_incorporation: null,
        principal_activity: "",
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={Step1ValidationSchema}
            onSubmit={async (values, actions) => {
                if (isEqual(values, initialValues)) {
                    onSkip();
                } else {
                    onNext(values, actions);
                }
            }}
            validateOnMount={false} // Prevents validation loop during initial render
            validateOnChange={false}
            validateOnBlur={true}
            enableReinitialize={true}
            readOnly={readOnly}
        >
            {({ values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting: formikSubmitting }) => (
                <Form autoComplete="off" noValidate onSubmit={readOnly ? undefined : handleSubmit}>
                    <Grid container spacing={4} sx={{ py: 2, px: 2 }}>
                        {/* ---------------------------------------------------- */}
                        {/* BUSINESS REGISTRATION (Copied from original file) */}
                        {/* ---------------------------------------------------- */}
                        <Grid item sm={6} xs={12}>
                            <Typography variant="h6" my={1}>
                                Business Registration
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                {/* Registered Name */}
                                <Grid item xs={4}>
                                    <Typography align="right">Registered Name*</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <TextField
                                        label="Registered Name"
                                        margin="dense"
                                        required
                                        fullWidth
                                        id="business_name"
                                        name="business_name"
                                        size="small"
                                        color="secondary"
                                        value={values.business_name}
                                        onChange={(e) => {
                                            const uppercasedValue = e.target.value.toUpperCase();
                                            setFieldValue("business_name", uppercasedValue)
                                        }}
                                        onBlur={handleBlur}
                                        error={Boolean(touched.business_name && errors.business_name)}
                                        helperText={touched.business_name && errors.business_name}
                                        disabled={isSubmitting || readOnly}
                                    />
                                </Grid>

                                {/* Registered ID (UEN) */}
                                <Grid item xs={4}>
                                    <Typography align="right">Registered ID*</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <TextField
                                        label="Registered ID"
                                        margin="dense"
                                        required
                                        fullWidth
                                        id="uen"
                                        name="uen"
                                        size="small"
                                        color="secondary"
                                        value={values.uen}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(touched.uen && errors.uen)}
                                        helperText={touched.uen && errors.uen}
                                        disabled={isSubmitting || readOnly}
                                        inputProps={{ maxLength: 13 }}
                                    />
                                </Grid>

                                {/* Registered Country */}
                                <Grid item xs={4}>
                                    <Typography align="right">Registered Country*</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    {countryList && (
                                        <Autocomplete
                                            id="registered_country"
                                            name="registered_country"
                                            options={countryList}
                                            getOptionLabel={(option) => MAPPED_REGISTERED_COUNTRY[option.registered_country]}
                                            value={countryList.find(option => option.id === values.registered_country) || null}
                                            onChange={(event, newValue) => {
                                                setFieldValue("registered_country", newValue ? newValue.id : "");
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Registered Country"
                                                    size="small"
                                                    margin="dense"
                                                    error={Boolean(touched.registered_country && errors.registered_country)}
                                                    helperText={touched.registered_country && errors.registered_country}
                                                    disabled={isSubmitting || readOnly}
                                                />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                        />
                                    )}
                                </Grid>
                                {/* Date of Incorporation */}
                                <Grid item xs={4}>
                                    <Typography align="right">Date of Incorporation</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <CustomDatePicker
                                        value={values.date_of_incorporation}
                                        onChange={(val) => setFieldValue("date_of_incorporation", val ? moment(val).format("YYYY-MM-DD") : null)}
                                        error={Boolean(touched.date_of_incorporation && errors.date_of_incorporation)}
                                        helperText={touched.date_of_incorporation && errors.date_of_incorporation}
                                        disabled={isSubmitting || readOnly}
                                        disableFuture={false}
                                    />
                                </Grid>

                                {/* Principal Activity (ACRA) */}
                                <Grid item xs={4}>
                                    <Typography align="right">Principal Activity (ACRA)</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <TextField
                                        label="Principal Activity"
                                        margin="dense"
                                        fullWidth
                                        id="principal_activity"
                                        name="principal_activity"
                                        size="small"
                                        color="secondary"
                                        multiline
                                        rows={4}
                                        inputProps={{ maxLength: 320 }}
                                        value={values.principal_activity}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(touched.principal_activity && errors.principal_activity)}
                                        helperText={touched.principal_activity && errors.principal_activity}
                                        disabled={isSubmitting || readOnly}
                                    />
                                </Grid>
                            </Grid>

                            {/* Registered Address */}
                            <Typography variant="h6" my={1} mt={2}>
                                Registered Address
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                {/* Address 1, 2, 3, Postal Code, Country (All copied directly) */}
                                <Grid item xs={4}><Typography align="right">Address 1*</Typography></Grid>
                                <Grid item xs={8}><TextField label="Address 1" margin="dense" required fullWidth id="address_1" name="address_1" size="small" color="secondary" value={values.address_1} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.address_1 && errors.address_1)} helperText={touched.address_1 && errors.address_1} disabled={isSubmitting || readOnly} /></Grid>
                                <Grid item xs={4}><Typography align="right">Address 2*</Typography></Grid>
                                <Grid item xs={8}><TextField label="Address 2" required fullWidth id="address_2" name="address_2" size="small" color="secondary" value={values.address_2} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.address_2 && errors.address_2)} helperText={touched.address_2 && errors.address_2} disabled={isSubmitting || readOnly} /></Grid>
                                <Grid item xs={4}><Typography align="right">Address 3</Typography></Grid>
                                <Grid item xs={8}><TextField label="Address 3" margin="dense" fullWidth id="address_3" name="address_3" size="small" color="secondary" value={values.address_3} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.address_3 && errors.address_3)} helperText={touched.address_3 && errors.address_3} disabled={isSubmitting || readOnly} /></Grid>
                                <Grid item xs={4}><Typography align="right">Postal Code</Typography></Grid>
                                <Grid item xs={8}><TextField label="Postal Code" fullWidth name="postal_code" size="small" margin="dense" value={values.postal_code} onChange={handleChange} disabled={isSubmitting || readOnly} /></Grid>
                                <Grid item xs={4}><Typography align="right">Country</Typography></Grid>
                                <Grid item xs={8}>
                                    <Autocomplete
                                        id="address_country"
                                        name="address_country"
                                        options={INTERNATIONAL_COUNTRY_LIST}
                                        getOptionLabel={(option) => option.name}
                                        value={INTERNATIONAL_COUNTRY_LIST.find(option => option.name === values.address_country?.toUpperCase()) || null}
                                        onChange={(event, newValue) => {
                                            setFieldValue("address_country", newValue ? newValue.name : "");
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Country"
                                                size="small"
                                                margin="dense"
                                                error={Boolean(touched.address_country && errors.address_country)}
                                                helperText={touched.address_country && errors.address_country}
                                                disabled={isSubmitting || readOnly}
                                            />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* ---------------------------------------------------- */}
                        {/* OFFICIAL CONTACT & MORE DETAILS (Copied from original file) */}
                        {/* ---------------------------------------------------- */}
                        <Grid item sm={6} xs={12}>
                            <Typography variant="h6" my={1}>
                                Official Contact
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                {/* Name, Contact Number (Autocomplete), Email (All copied directly) */}
                                <Grid item xs={4}><Typography align="right">Name*</Typography></Grid>
                                <Grid item xs={8}><TextField label="Name" margin="dense" fullWidth id="contact_name" name="contact_name" size="small" color="secondary" value={values.contact_name} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.contact_name && errors.contact_name)} helperText={touched.contact_name && errors.contact_name} disabled={isSubmitting || readOnly} /></Grid>

                                {/* Contact Number (country code selector + number input) */}
                                <Grid item xs={4}><Typography align="right">Contact Number</Typography></Grid>
                                <Grid item xs={8}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                        <Autocomplete
                                            sx={{ width: 155, flexShrink: 0 }}
                                            options={COUNTRIES}
                                            autoHighlight
                                            value={COUNTRIES.find(c => values.contact_number?.startsWith(c.dialCode)) || null}
                                            isOptionEqualToValue={(opt, val) => opt.code === val.code}
                                            getOptionLabel={(opt) => opt?.dialCode || ''}
                                            onChange={(event, newValue) => {
                                                const prevCountry = COUNTRIES.find(c => values.contact_number?.startsWith(c.dialCode));
                                                const localNum = prevCountry
                                                    ? (values.contact_number?.slice(prevCountry.dialCode.length) || '')
                                                    : (values.contact_number || '');
                                                setFieldValue('contact_number', newValue ? newValue.dialCode + localNum : localNum);
                                            }}
                                            renderOption={(props, option) => (
                                                <li {...props}>
                                                    <img
                                                        loading="lazy"
                                                        width="20"
                                                        src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                                        srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                                        alt=""
                                                        style={{ marginRight: 8 }}
                                                    />
                                                    {option.label} ({option.dialCode})
                                                </li>
                                            )}
                                            renderInput={(params) => {
                                                const sel = COUNTRIES.find(c => values.contact_number?.startsWith(c.dialCode));
                                                return (
                                                    <TextField
                                                        {...params}
                                                        label="Country"
                                                        size="small"
                                                        color="secondary"
                                                        margin="dense"
                                                        disabled={isSubmitting || readOnly}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            startAdornment: sel ? (
                                                                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                                                    <img
                                                                        loading="lazy"
                                                                        width="20"
                                                                        src={`https://flagcdn.com/w20/${sel.code.toLowerCase()}.png`}
                                                                        srcSet={`https://flagcdn.com/w40/${sel.code.toLowerCase()}.png 2x`}
                                                                        alt={sel.code}
                                                                    />
                                                                </InputAdornment>
                                                            ) : params.InputProps.startAdornment,
                                                        }}
                                                    />
                                                );
                                            }}
                                            disabled={isSubmitting || readOnly}
                                        />
                                        <TextField
                                            label="Number"
                                            size="small"
                                            color="secondary"
                                            margin="dense"
                                            fullWidth
                                            name="contact_number"
                                            value={(() => {
                                                const sel = COUNTRIES.find(c => values.contact_number?.startsWith(c.dialCode));
                                                return sel
                                                    ? (values.contact_number?.slice(sel.dialCode.length) || '')
                                                    : (values.contact_number || '');
                                            })()}
                                            onChange={(e) => {
                                                const digits = e.target.value.replace(/\D/g, '').slice(0, 15);
                                                const sel = COUNTRIES.find(c => values.contact_number?.startsWith(c.dialCode));
                                                setFieldValue('contact_number', (sel?.dialCode || '') + digits);
                                            }}
                                            onBlur={handleBlur}
                                            inputProps={{ inputMode: 'numeric' }}
                                            disabled={isSubmitting || readOnly}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={4}><Typography align="right">Email*</Typography></Grid>
                                <Grid item xs={8}><TextField label="Email" margin="dense" fullWidth id="contact_email" name="contact_email" size="small" color="secondary" value={values.contact_email} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.contact_email && errors.contact_email)} helperText={touched.contact_email && errors.contact_email} disabled={isSubmitting || readOnly} /></Grid>
                            </Grid>

                            {/* More Details */}
                            <Typography variant="h6" my={1}>
                                More Details
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                {/* Application Type */}
                                <Grid item xs={4}><Typography align="right">Application Type*</Typography></Grid>
                                <Grid item xs={8}>

                                    <Autocomplete
                                        id="application_type"
                                        name="application_type"
                                        options={APPLICATION_TYPES}
                                        getOptionLabel={(option) => option.label}
                                        value={APPLICATION_TYPES.find(option => option.label === values.application_type) || null}
                                        onChange={(event, newValue) => {
                                            setFieldValue("application_type", newValue ? newValue.value : null);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Application Type"
                                                size="small"
                                                margin="dense"
                                                error={Boolean(touched.application_type && errors.application_type)}
                                                helperText={touched.application_type && errors.application_type}
                                                disabled={isSubmitting}
                                            />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                    />
                                </Grid>

                                {/* Number of Merchants (CRITICAL for Step 2) */}
                                <Grid item xs={4}><Typography align="right">Number of merchant(s)*</Typography></Grid>
                                <Grid item xs={8}><TextField required label="Number of merchant(s)" margin="dense" fullWidth id="number_of_merchants" name="number_of_merchants" size="small" color="secondary" type="number" InputProps={{ inputProps: { min: 1 } }} value={values.number_of_merchants} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.number_of_merchants && errors.number_of_merchants)} helperText={touched.number_of_merchants && errors.number_of_merchants} disabled={isSubmitting || readOnly} /></Grid>

                                {/* Data Folder, Merchant Payout, Bank Account (All copied directly) */}
                                <Grid item xs={4}><Typography align="right">Data Folder</Typography></Grid>
                                <Grid item xs={8}><TextField label="Data Folder" margin="dense" fullWidth id="gdrive_link" name="gdrive_link" size="small" color="secondary" multiline maxRows={10} value={values.gdrive_link} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.gdrive_link && errors.gdrive_link)} helperText={touched.gdrive_link && errors.gdrive_link} disabled={isSubmitting || readOnly} /></Grid>

                                {/* Merchant Payout (Select logic retained) */}
                                <Grid item xs={4}><Typography align="right">Merchant Payout</Typography></Grid>
                                <Grid item xs={8}>
                                    <Select
                                        id={"merchant_payout"} name={"merchant_payout"} getOptionLabel={(option) => option.label} getOptionValue={(option) => option.id} closeMenuOnSelect={false} isMulti
                                        options={entityFilterConditions?.merchant_payout?.filter(item => item && item.merchant_id && item.merchant_name).map((item) => ({ id: item.merchant_id, label: `${item.merchant_name} (${item.source})`, source: item.source, })) || []}
                                        value={values.merchant_payout || []}
                                        onChange={(event) => setFieldValue("merchant_payout", event)}
                                        styles={{ control: (provided) => ({ ...provided, }), menuPortal: (base) => ({ ...base, zIndex: 9999, }), }} menuPortalTarget={document.body}
                                    />
                                </Grid>

                                {/* Bank Account (Select logic retained) */}
                                {/* <Grid item xs={4}><Typography align="right">Bank Account</Typography></Grid>
                                <Grid item xs={8}>
                                    <Select
                                        id={"bank_accounts"} name={"bank_accounts"} getOptionLabel={(option) => option.label} getOptionValue={(option) => option.id} closeMenuOnSelect={false} isMulti
                                        options={bankAccounts?.map((item) => ({ id: item.id, label: `${item.account_number} (${item.bank})`, })) || []}
                                        value={values.bank_accounts || []}
                                        onChange={(event) => setFieldValue("bank_accounts", event)}
                                        styles={{ control: (provided) => ({ ...provided, }), menuPortal: (base) => ({ ...base, zIndex: 9999, }), }} menuPortalTarget={document.body}
                                    />
                                </Grid> */}
                            </Grid>
                        </Grid>

                        {/* Submit Button */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                {/* Back button for Stepper is in the parent/Dialog footer if needed */}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isSubmitting || readOnly}
                                    sx={{ ...styles.customButton }}
                                >
                                    {isSubmitting ? 'Saving...' : readOnly ? 'Read Only' : 'Save & Continue'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    );
};

export default Step1BusinessEntity;